import { useState } from "react";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  Stack,
  NumberInput,
  Select,
  Button,
  Text,
  rem,
  ThemeIcon,
  Group,
  Paper,
  Divider,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconTrophy, IconCheck } from "@tabler/icons-react";
import { isNumber } from "lodash";
import {
  MatchScoreStatus,
  UpdateMatchScoreInput,
} from "../../../../../gql/graphql";

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: rem(280),
  },
  teamCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
  },
  teamName: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  updateButton: {
    fontWeight: 600,
    marginTop: theme.spacing.lg,
  },
  scoreInput: {
    "& input": {
      fontSize: theme.fontSizes.lg,
      fontWeight: 700,
      textAlign: "center",
    },
  },
}));

interface TeamScoreEntry {
  teamId: string;
  teamName: string;
  score: number;
  status: string;
}

interface GameScoreModalProps {
  matchId: string;
  teams: TeamScoreEntry[];
  onSuccess?: () => void;
}

const UPDATE_MATCH_SCORES_MUTATION = gql`
  mutation ($matchId: ID!, $input: [UpdateMatchScoreInput!]!) {
    updateMatchScores(matchId: $matchId, input: $input) {
      id
    }
  }
`;

const statusOptions = Object.values(MatchScoreStatus).map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase(),
}));

export const GameScoreModal = ({
  matchId,
  teams,
  onSuccess,
}: GameScoreModalProps) => {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);

  const [teamScores, setTeamScores] = useState<
    {
      teamId: string;
      teamName: string;
      score: number;
      status: MatchScoreStatus;
    }[]
  >(
    teams.map((t) => ({
      teamId: t.teamId,
      teamName: t.teamName,
      score: t.score,
      status: (t.status as MatchScoreStatus) ?? MatchScoreStatus.Waiting,
    })),
  );

  const [updateMatchScores] = useMutation<
    { updateMatchScores: { id: string } },
    { matchId: string; input: UpdateMatchScoreInput[] }
  >(UPDATE_MATCH_SCORES_MUTATION);

  const updateTeamField = (
    index: number,
    field: "score" | "status",
    value: number | MatchScoreStatus,
  ) => {
    setTeamScores((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry,
      ),
    );
  };

  const handleUpdateAll = async () => {
    setLoading(true);
    try {
      const input: UpdateMatchScoreInput[] = teamScores.map((entry) => ({
        teamId: entry.teamId,
        forcedScoreValue: entry.score,
        status: entry.status,
      }));

      const result = await updateMatchScores({
        variables: {
          matchId,
          input,
        },
      });

      if (result?.error) {
        throw new Error(result.error.message);
      }

      notifications.show({
        title: "Scores Updated",
        message: `Updated scores for ${teamScores.length} teams`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      onSuccess?.();
      modals.closeAll();
    } catch (error) {
      notifications.show({
        title: "Update Failed",
        message:
          error instanceof Error ? error.message : "Failed to update scores",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack className={classes.container}>
      {/* Header */}
      <Group>
        <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
          <IconTrophy size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600}>Update Game Scores</Text>
          <Text size="xs" c="dimmed">
            Modify scores and statuses for all teams in this game
          </Text>
        </div>
      </Group>

      <Divider />

      {/* Team Score Cards */}
      <Stack gap="sm">
        {teamScores.map((entry, index) => (
          <Paper key={entry.teamId} className={classes.teamCard}>
            <Text className={classes.teamName} mb="sm">
              {entry.teamName}
            </Text>
            <Group grow>
              <NumberInput
                className={classes.scoreInput}
                label="Score"
                value={entry.score}
                min={0}
                onChange={(value) => {
                  updateTeamField(index, "score", isNumber(value) ? value : 0);
                }}
                size="sm"
              />
              <Select
                label="Status"
                data={statusOptions}
                value={entry.status}
                onChange={(value) => {
                  if (value) {
                    updateTeamField(index, "status", value as MatchScoreStatus);
                  }
                }}
                size="sm"
              />
            </Group>
          </Paper>
        ))}
      </Stack>

      {/* Update All Button */}
      <Button
        className={classes.updateButton}
        loading={loading}
        onClick={handleUpdateAll}
        leftSection={<IconCheck size={16} />}
        fullWidth
      >
        Update All
      </Button>
    </Stack>
  );
};

export const openGameScoreModal = (props: GameScoreModalProps) => {
  modals.open({
    title: null,
    centered: true,
    size: "lg",
    children: <GameScoreModal {...props} />,
  });
};
